import { Text, View } from 'react-native'
import { ScreenLoadingFallback } from '@/src/components/ui/ScreenLoadingFallback'
import { useTranslation } from 'react-i18next'

import { useVoiceAssessmentFlowController } from './useVoiceAssessmentFlowController'
import { VoiceAssessmentFlowView } from './VoiceAssessmentFlowView'

interface VoiceAssessmentFlowProps {
  /**
   * Backend `/api/session` id that drives the flow. The intro
   * UI for a brand-new assessment lives on the standalone
   * `/voice-assessment` route; by the time this component renders we
   * always have a real backend session.
   */
  backendSessionId: number
  isOnboardingEntry?: boolean
}

function renderErrorState(message: string) {
  return (
    <View className="flex-1 items-center justify-center px-6">
      <Text className="mb-2 text-lg text-destructive">{message}</Text>
      <Text className="text-base text-textBody text-center">{message}</Text>
    </View>
  )
}

export function VoiceAssessmentFlow({
  backendSessionId,
  isOnboardingEntry = false,
}: VoiceAssessmentFlowProps) {
  const { t } = useTranslation(['common', 'onboarding'])
  const {
    backendSessionQuery,
    controller,
    sessionDefinitionQuery,
    shouldRejectSession,
  } = useVoiceAssessmentFlowController({
    backendSessionId,
    isOnboardingEntry,
  })

  if (
    shouldRejectSession ||
    backendSessionQuery.isLoading ||
    sessionDefinitionQuery.isLoading
  ) {
    return <ScreenLoadingFallback />
  }

  if (backendSessionQuery.error != null) {
    return renderErrorState(t('common:error'))
  }

  if (sessionDefinitionQuery.error != null) {
    return renderErrorState(t('common:error'))
  }

  if (controller.measurementCount === 0) {
    return (
      <View className="flex-1 items-center justify-center px-6">
        <Text className="text-base text-textBody text-center">
          {t('onboarding:voice_assessment.no_measurements_message')}
        </Text>
      </View>
    )
  }

  return (
    <VoiceAssessmentFlowView
      controller={controller}
      isOnboardingEntry={isOnboardingEntry}
    />
  )
}
